import { Box, Button } from "@mui/material";
import { GetAllAssetsLocationsQuery, Status } from "../../../graphql/generated";
import toLatLng from "../../../utils/geo/toLaLng";
import Map, {
  createMarker,
  InfoWindowContent,
  MarkerData,
} from "../../common/Map/Map";
import trailerPin from "../../../assets/icons/trailer-pin.svg";
import tractorPin from "../../../assets/icons/tractor-pin.svg";
import truckPin from "../../../assets/icons/truck-pin.svg";
import driverPin from "../../../assets/icons/driver-pin.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import { groupBy, map, minBy } from "lodash";
import { differenceInSeconds } from "date-fns";
import { LiveTrackerTripData } from "../LiveTripsTracker/LiveTripsTracker";
import MapControl from "./MapControl";
import { usePrevious } from "../../../utils/hooks/usePrevious";
import LinkedAssetHeader from "../LinkedAssetHeader/LinkedAssetHeader";
import TripLiveSummary from "../TripLiveSummary/TripLiveSummary";
import driverLabel from "../../../utils/labels/driverLabel";
import tractorLabel from "../../../utils/labels/tractorLabel";
import trailerLabel from "../../../utils/labels/trailerLabel";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import { testIds } from "./AssetTrackingMap.testIds";

type TrailerIdentificationLocationData =
  GetAllAssetsLocationsQuery["trailers"]["data"][0];
type TractorIdentificationLocationData =
  GetAllAssetsLocationsQuery["tractors"]["data"][0] & {
    type?: never;
  };
type DriverIdentificationLocationData =
  GetAllAssetsLocationsQuery["drivers"]["data"][0] & {
    type?: never;
    serialNumber?: never;
  };

export type AssetTrackingMapProps = {
  trailers: TrailerIdentificationLocationData[];
  tractors: TractorIdentificationLocationData[];
  drivers: DriverIdentificationLocationData[];
  trips: LiveTrackerTripData[];
  selectedTrip?: LiveTrackerTripData | null;
  selectedDriverId?: string | null;
  selectedTractorId?: string | null;
  selectedTrailerId?: string | null;
  onTripSelect?: (trip: LiveTrackerTripData) => void;
  onTripUnselect?: () => void;
};

const AssetTrackingMap = ({
  trailers,
  tractors,
  drivers,
  trips,
  selectedTrip,
  selectedDriverId,
  selectedTractorId,
  selectedTrailerId,
  onTripSelect,
  onTripUnselect,
}: AssetTrackingMapProps) => {
  const [isSummarizedView, setIsSummarizedView] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  const center = useMemo(
    () => ({ lat: 49.90243189340297, lng: -100.95859946285935 }),
    []
  );

  const linkedAssets = useMemo(() => {
    return map(
      groupBy(
        [
          ...trailers.map((trailer) => ({ ...trailer, kind: "TRAILER" })),
          ...tractors.map((tractor) => ({ ...tractor, kind: "TRACTOR" })),
          ...drivers.map((driver) => ({ ...driver, kind: "DRIVER" })),
        ],
        (asset) =>
          `${asset.lastKnownLocation?.latitude}::${asset.lastKnownLocation?.longitude}::${asset.lastKnownLocationDate}`
      ),
      (assetGroup) => {
        const trailer = assetGroup.find((a) => a.kind === "TRAILER") as
          | TrailerIdentificationLocationData | null | undefined;
        const tractor = assetGroup.find((a) => a.kind === "TRACTOR") as
          | TractorIdentificationLocationData | null | undefined;
        const driver = assetGroup.find((a) => a.kind === "DRIVER") as
          | DriverIdentificationLocationData | null | undefined;
        const trip =
          (trailer ? getAssetCurrentOrNextTrip(trips, trailer, "TRAILER") : null) ||
          (tractor ? getAssetCurrentOrNextTrip(trips, tractor, "TRACTOR") : null) ||
          (driver ? getAssetCurrentOrNextTrip(trips, driver, "DRIVER") : null);
        return {
          _id: [trailer?._id, tractor?._id, driver?._id].filter(Boolean).join("::"),
          trailer,
          tractor,
          driver,
          lastKnownLocation:
            trailer?.lastKnownLocation ||
            tractor?.lastKnownLocation ||
            driver?.lastKnownLocation,
          lastKnownLocationDate:
            trailer?.lastKnownLocationDate ||
            tractor?.lastKnownLocationDate ||
            driver?.lastKnownLocationDate,
          trip,
        };
      }
    );
  }, [trailers, tractors, drivers, trips]);

  const previousSelectedTrip = usePrevious(selectedTrip);
  useEffect(() => {
    if (selectedTrip) {
      const assets = linkedAssets.filter(
        (a) =>
          a.trailer?._id === selectedTrip.trailer?._id ||
          a.driver?._id === selectedTrip.driver?._id ||
          a.tractor?._id === selectedTrip.tractor?._id
      );
      const loc = assets[0]?.lastKnownLocation;
      if (loc) {
        mapRef.current?.panTo({ lat: loc.latitude, lng: loc.longitude });
        const zoom = mapRef.current?.getZoom();
        if (zoom && zoom < 10) mapRef.current?.setZoom(10);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrip, previousSelectedTrip]);

  const previousSelectedDriver = usePrevious(selectedDriverId);
  useEffect(() => {
    if (selectedDriverId) {
      const assets = linkedAssets.filter((a) => a.driver?._id === selectedDriverId);
      const loc = assets[0]?.lastKnownLocation;
      if (loc) {
        mapRef.current?.panTo({ lat: loc.latitude, lng: loc.longitude });
        const zoom = mapRef.current?.getZoom();
        if (zoom && zoom < 10) mapRef.current?.setZoom(10);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverId, previousSelectedDriver]);

  const previousSelectedTractor = usePrevious(selectedTractorId);
  useEffect(() => {
    if (selectedTractorId) {
      const assets = linkedAssets.filter((a) => a.tractor?._id === selectedTractorId);
      const loc = assets[0]?.lastKnownLocation;
      if (loc) {
        mapRef.current?.panTo({ lat: loc.latitude, lng: loc.longitude });
        const zoom = mapRef.current?.getZoom();
        if (zoom && zoom < 10) mapRef.current?.setZoom(10);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTractorId, previousSelectedTractor]);

  const previousSelectedTrailer = usePrevious(selectedTrailerId);
  useEffect(() => {
    if (selectedTrailerId) {
      const assets = linkedAssets.filter((a) => a.trailer?._id === selectedTrailerId);
      const loc = assets[0]?.lastKnownLocation;
      if (loc) {
        mapRef.current?.panTo({ lat: loc.latitude, lng: loc.longitude });
        const zoom = mapRef.current?.getZoom();
        if (zoom && zoom < 10) mapRef.current?.setZoom(10);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrailerId, previousSelectedTrailer]);

  const markers: MarkerData[] = useMemo(() => {
    return linkedAssets
      .filter((linkedAsset) => {
        if (!linkedAsset.lastKnownLocation) return false;
        if (isSummarizedView && !linkedAsset.trip) return false;
        return true;
      })
      .map((linkedAsset) => {
        const linkedAssetTrip = linkedAsset.trip;
        const otherLinkedAssetsForTrip = linkedAssets.filter(
          (other) =>
            other.trip &&
            other.trip._id === linkedAssetTrip?._id &&
            other._id !== linkedAsset._id
        );
        const shouldDisplayTrip =
          otherLinkedAssetsForTrip.length === 0 ||
          !otherLinkedAssetsForTrip.find(
            (other) => other.lastKnownLocationDate > linkedAsset.lastKnownLocationDate
          );

        const iconUrl =
          linkedAsset.trailer && linkedAsset.tractor
            ? truckPin
            : linkedAsset.trailer
            ? trailerPin
            : linkedAsset.tractor
            ? tractorPin
            : linkedAsset.driver
            ? driverPin
            : truckPin;

        return createMarker(
          toLatLng(linkedAsset.lastKnownLocation!),
          {
            icon: {
              url: iconUrl,
              anchor: new window.google.maps.Point(17, 17),
            },
          },
          () => {
            if (linkedAsset.trip) {
              onTripSelect?.(linkedAsset.trip);
            }
          },
          {
            content: (
              <>
                <LinkedAssetHeader
                  linkedAsset={linkedAsset}
                  isSummarizedView={isSummarizedView}
                />
                {linkedAsset.trip && shouldDisplayTrip ? (
                  <TripLiveSummary
                    trip={linkedAsset.trip}
                    isSummarizedView={isSummarizedView}
                  />
                ) : null}
              </>
            ),
            extensionContent: buildAssetExtensionContent(linkedAsset, shouldDisplayTrip),
            onCloseClick: () => {
              if (selectedTrip) onTripUnselect?.();
            },
          }
        );
      });
  }, [linkedAssets, isSummarizedView, onTripSelect, onTripUnselect, selectedTrip]);

  return (
    <Box sx={{ height: "100%" }} id="asset-tracking-map-container">
      <Map
        containerStyle={{ height: "100%" }}
        center={center}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        zoom={4}
        markers={markers}
        clustered
        useStandardMap
      >
        <MapControl position={window.google.maps.ControlPosition.TOP_RIGHT}>
          <Button data-testid={testIds.assetTrackingMapToggleSummaryView}
            variant="contained"
            color="lightOrange"
            onClick={() => setIsSummarizedView(!isSummarizedView)}
            sx={{ mr: 1, mt: 1 }}
          >
            {!isSummarizedView ? "Summary View" : "Detail View"}
          </Button>
        </MapControl>
      </Map>
    </Box>
  );
};

function buildAssetExtensionContent(
  linkedAsset: {
    trailer?: TrailerIdentificationLocationData | null;
    tractor?: TractorIdentificationLocationData | null;
    driver?: DriverIdentificationLocationData | null;
    lastKnownLocationDate?: Date | string | null;
    trip?: { _id: string; tripNumber?: string | null } | null;
  },
  shouldDisplayTrip: boolean
): InfoWindowContent {
  const content: InfoWindowContent = [
    {
      type: "text",
      value: `${tractorLabel(linkedAsset.tractor)} / ${trailerLabel(linkedAsset.trailer)}`,
      bold: true,
    },
  ];
  const driver = driverLabel(linkedAsset.driver);
  if (driver && driver !== "N/A") {
    content.push({ type: "text", value: driver });
  }
  if (linkedAsset.lastKnownLocationDate) {
    content.push({
      type: "chip",
      label: `Last seen at ${formatDateTime(new Date(linkedAsset.lastKnownLocationDate as string))}`,
    });
  }
  if (linkedAsset.trip && shouldDisplayTrip && linkedAsset.trip.tripNumber) {
    content.push({
      type: "link",
      label: `Trip ${linkedAsset.trip.tripNumber}`,
      href: `/trips/${linkedAsset.trip._id}`,
    });
  }
  return content;
}

const getAssetCurrentOrNextTrip = (
  trips: LiveTrackerTripData[],
  asset:
    | TractorIdentificationLocationData
    | TrailerIdentificationLocationData
    | DriverIdentificationLocationData,
  type: "TRACTOR" | "TRAILER" | "DRIVER"
): LiveTrackerTripData | null => {
  const assetTrips = trips.filter(
    (trip) =>
      (type === "TRACTOR" && trip.tractor?._id === asset._id) ||
      (type === "TRAILER" && trip.trailer?._id === asset._id) ||
      (type === "DRIVER" && trip.driver?._id === asset._id)
  );
  const closestTrip = minBy(assetTrips, (trip) => {
    if (trip.status === Status.InProgress) return 0;
    const firstPickupTime = new Date(trip.shipmentLocations[0].arrivalTime);
    const now = new Date();
    if (firstPickupTime < now) return 86400 + differenceInSeconds(now, firstPickupTime);
    return differenceInSeconds(firstPickupTime, now);
  });
  return closestTrip || null;
};

export default AssetTrackingMap;
